import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient, type InfiniteData } from 'react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Search, Plus, Calendar, Hash, Sparkles, Trash2, Grid3X3, List, ArrowUp, ArrowDown, Upload, Download, ChevronDown, ChevronUp, AudioLines } from 'lucide-react';
import { useAnalytics } from '../hooks/useAnalytics';
import { useSearchTracking } from '../hooks/useSearchTracking';
import { apiService, Memory, MemoryList, Space } from '../services/api';

import { ResourceLimitGate, UsageWarning } from '../components/FeatureGate';
import MemoryGridView from '../components/MemoryGridView';
import { useActivityTimeline } from '../hooks/useActivityTimeline';
import MemoryTypesBadges from '../components/ui/MemoryTypesBadges';
import MemoryDetailModal from '../components/MemoryDetailModal';
import { useMemoryStream } from '../hooks/useMemoryStream';
import { useTranslation } from 'react-i18next';
import { ConfirmationDialog } from '../components/ui/ConfirmationDialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useMemorySearchQuery } from '../hooks/useMemoriesQuery';
import {
  useInfiniteMemoriesQuery,
  applyMemoryUpdatedToInfiniteData,
  applyMemoryRemovedFromInfiniteData,
} from '../hooks/useInfiniteMemoriesQuery';
import {
  useSpaceMemoryCount,
  spaceMemoryCountQueryKey,
} from '../hooks/useSpaceMemoryCount';
import { useTier } from '../contexts/TierContext';
import { canUploadAudioByTier } from '../lib/audioUploadEligibility';
import { memoryHasAudio } from '../lib/memoryMedia';
import {
  MemoryWorkspaceAudioAttach,
  type PendingWorkspaceAudio,
} from '../components/memories/MemoryWorkspaceAudioAttach';
import { extractErrorMessage } from '../utils/errorUtils';
import { cn } from '@/lib/utils';
import { useFeedback } from '../context/FeedbackContext';

/** Overrides shadcn outline `bg-background` so buttons read on dark workspace cards */
const WORKSPACE_OUTLINE_BUTTON =
  'border-workspace-border bg-workspace-surface-secondary/40 text-workspace-text-primary hover:bg-workspace-surface-tertiary/80 hover:text-workspace-text-primary';

interface MemoriesPageProps {
  spaceContext?: Space;
  searchInputRef?: React.RefObject<HTMLInputElement>;
  showTagFilters?: boolean;
}

export default function MemoriesPage({ 
  spaceContext, 
  searchInputRef, 
  showTagFilters 
}: MemoriesPageProps) {
  const analytics = useAnalytics();
  const trackEvent = analytics.trackEvent;
  const { trackSearch } = useSearchTracking();
  const { trackMemoryCreated, trackSearchPerformed } = useActivityTimeline();
  const { t } = useTranslation();
  const { showFeedback } = useFeedback();
  const navigate = useNavigate();
  const { getCurrentTier, getNextTier } = useTier();
  const currentTier = getCurrentTier();
  const nextTier = getNextTier() || 'starter';
  const canUploadAudio = canUploadAudioByTier(currentTier);
  const spaceId = spaceContext?.space_id;
  const spaceName = spaceContext?.name;
  
  const [newMemory, setNewMemory] = useState('');
  const [newTags, setNewTags] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [errors, setErrors] = useState({ memory: '' });
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('grid');
  const [selectedMemoryId, setSelectedMemoryId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTagFilters, setSelectedTagFilters] = useState<string[]>([]);
  const [selectedSourceFilter, setSelectedSourceFilter] = useState<string>('all');
  const [minImportanceFilter, setMinImportanceFilter] = useState<number | null>(null);
  const [sortBy, setSortBy] = useState<'created_at' | 'updated_at' | 'importance'>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const { count: totalMemoryCount } = useSpaceMemoryCount(spaceContext?.space_id);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{ isOpen: boolean; memoryId: string | null }>({
    isOpen: false,
    memoryId: null
  });
  const [showMultimodalRoadmap, setShowMultimodalRoadmap] = useState(false);
  const [pendingAudio, setPendingAudio] = useState<PendingWorkspaceAudio>(null);
  const [showTagFiltersLocal, setShowTagFiltersLocal] = useState(false);

  const queryClient = useQueryClient();
  const loadMoreSentinelRef = useRef<HTMLDivElement | null>(null);
  const shouldShowTagFilters = showTagFilters ?? showTagFiltersLocal;

  const infiniteMemoriesQuery = useInfiniteMemoriesQuery({
    spaceId: spaceContext?.space_id,
    sortBy,
    sortOrder,
    tags: selectedTagFilters,
    source: selectedSourceFilter !== 'all' ? selectedSourceFilter : undefined,
    minImportance: minImportanceFilter ?? undefined,
    enabled: spaceContext !== undefined,
  });

  const searchQueryHook = useMemorySearchQuery({
    spaceId: spaceContext?.space_id,
    enabled: spaceContext !== undefined
  });

  const browseMemories = infiniteMemoriesQuery.memories;
  const searchMemoriesList = searchQueryHook.memories;
  const memories = searchQueryHook.isSearching ? searchMemoriesList : browseMemories;
  const isLoading = searchQueryHook.isSearching
    ? searchQueryHook.isLoading
    : infiniteMemoriesQuery.isLoading;

  const browseListTotal = infiniteMemoriesQuery.total;

  const refreshMemoryViews = useCallback(async () => {
    await infiniteMemoriesQuery.refetch();
    if (searchQueryHook.isSearching && searchQuery.trim()) {
      await searchQueryHook.search(searchQuery.trim());
    }
  }, [infiniteMemoriesQuery, searchQueryHook, searchQuery]);

  const infiniteQueryKey = infiniteMemoriesQuery.queryKey;

  // ✅ REAL-TIME: Sync infinite list with SSE (browse mode only)
  const handleMemoryCreated = useCallback(
    (_memory: Memory) => {
      queryClient.invalidateQueries(infiniteQueryKey, { exact: true });
      if (spaceContext?.space_id) {
        queryClient.invalidateQueries(spaceMemoryCountQueryKey(spaceContext.space_id));
      }
    },
    [queryClient, infiniteQueryKey, spaceContext?.space_id]
  );

  const handleMemoryUpdated = useCallback(
    (memory: Memory) => {
      queryClient.setQueryData(infiniteQueryKey, (old) =>
        applyMemoryUpdatedToInfiniteData(
          old as InfiniteData<MemoryList> | undefined,
          memory
        )
      );
    },
    [queryClient, infiniteQueryKey]
  );

  const handleMemoryDeleted = useCallback(
    (memoryId: string) => {
      queryClient.setQueryData(infiniteQueryKey, (old) =>
        applyMemoryRemovedFromInfiniteData(
          old as InfiniteData<MemoryList> | undefined,
          memoryId
        )
      );
      if (spaceContext?.space_id) {
        queryClient.invalidateQueries(spaceMemoryCountQueryKey(spaceContext.space_id));
      }
    },
    [queryClient, infiniteQueryKey, spaceContext?.space_id]
  );

  // ✅ REAL-TIME: Use memory stream for live updates
  useMemoryStream({
    spaceId: spaceContext?.space_id,
    enabled: spaceContext !== undefined, // Only enable when space context is available
    onMemoryCreated: handleMemoryCreated,
    onMemoryUpdated: handleMemoryUpdated,
    onMemoryDeleted: handleMemoryDeleted,
    onConnectionChange: (connected) => {
      console.log('🔄 SSE connection status:', connected ? 'connected' : 'disconnected');
    }
  });

  // Track page view
  useEffect(() => {
    if (spaceId !== undefined) {
      trackEvent('page_view', {
        page: 'memories',
        realm: 'workspace',
        spaceId,
        spaceName
      });
    }
  }, [trackEvent, spaceId, spaceName]);

  useEffect(() => {
    if (searchQueryHook.isSearching) return;
    const el = loadMoreSentinelRef.current;
    if (!el || !infiniteMemoriesQuery.hasNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (
          first.isIntersecting &&
          infiniteMemoriesQuery.hasNextPage &&
          !infiniteMemoriesQuery.isFetchingNextPage
        ) {
          infiniteMemoriesQuery.fetchNextPage();
        }
      },
      { root: null, rootMargin: '240px', threshold: 0 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [
    searchQueryHook.isSearching,
    infiniteMemoriesQuery.hasNextPage,
    infiniteMemoriesQuery.isFetchingNextPage,
    infiniteMemoriesQuery.fetchNextPage,
    browseMemories.length,
  ]);

  // Extract unique tags from all memories for filtering (memoized)
  const allTags = useMemo(() => {
    const tags = new Set<string>();
    memories.forEach(memory => {
      memory.tags.forEach(tag => tags.add(tag));
    });
    return Array.from(tags).sort();
  }, [memories]);

  const allSources = useMemo(() => {
    const sources = new Set<string>();
    memories.forEach((memory) => {
      if (memory.source) {
        sources.add(memory.source);
      }
    });
    return Array.from(sources).sort();
  }, [memories]);

  // ✅ REAL-TIME: Space-aware search using query hook
  const searchMemories = useCallback(async (query: string) => {
    if (!query.trim()) {
      // If empty query, clear search
      searchQueryHook.clearSearch();
      return;
    }

    const startTime = performance.now();
    
    try {
      await searchQueryHook.search(query);
      
      const responseTime = performance.now() - startTime;
      const searchResults = searchQueryHook.memories;
      
      // Track search with new analytics system
      trackSearch(query, searchResults.length, responseTime);
      
      // Track search in activity timeline
      trackSearchPerformed(query, searchResults.length);
      
      // Keep existing analytics for compatibility
      analytics.trackEvent('memory_search', {
        query,
        resultCount: searchResults.length,
        totalMemories: searchResults.length,
        spaceId: spaceContext?.space_id,
        spaceName: spaceContext?.name
      });
    } catch (error: any) {
      console.error('Search failed:', error);
      setErrors({ memory: t('errors.searchFailed', { ns: 'memories' }) as string });
    }
  }, [searchQueryHook, spaceContext?.space_id, spaceContext?.name, analytics, trackSearch, trackSearchPerformed, t]);

  // Debounced search - only trigger when searchQuery actually changes
  useEffect(() => {
    // Don't trigger on initial mount when searchQuery is empty
    if (searchQuery === '') {
      searchQueryHook.clearSearch();
      return;
    }

    const timer = setTimeout(() => {
      searchMemories(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, searchMemories, searchQueryHook]);

  // Space-aware memory creation (text JSON or multipart audio)
  const createMemory = async () => {
    const startTime = performance.now();
    const trimmed = newMemory.trim();

    if (!trimmed) {
      setErrors({ memory: t('errors.contentEmpty', { ns: 'memories' }) as string });
      analytics.trackEvent('memory_creation_failed', {
        reason: 'empty_content',
        validationError: true,
        spaceId: spaceContext?.space_id,
      });
      return;
    }

    if (trimmed.length < 3) {
      setErrors({
        memory: t('errors.contentTooShort', { ns: 'memories' }) as string,
      });
      analytics.trackEvent('memory_creation_failed', {
        reason: 'content_too_short',
        contentLength: trimmed.length,
        validationError: true,
        spaceId: spaceContext?.space_id,
      });
      return;
    }

    setErrors({ memory: '' });
    setIsCreating(true);

    analytics.trackEvent('memory_creation_started', {
      contentLength: trimmed.length,
      tagCount: newTags.split(',').filter((tg) => tg.trim()).length,
      spaceId: spaceContext?.space_id,
      spaceName: spaceContext?.name,
      hasAudio: Boolean(pendingAudio),
      audioSource: pendingAudio?.source ?? null,
    });

    try {
      const tags = newTags
        .split(',')
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0);

      let createdMemory: Memory;

      if (pendingAudio?.source === 'file') {
        createdMemory = await apiService.createAudioMemory({
          audioFile: pendingAudio.file,
          content: trimmed,
          tags,
          source: 'workspace_ui_audio_file',
          category: 'audio',
          importance: 5,
          spaceId: spaceContext?.space_id,
        });
      } else if (pendingAudio?.source === 'mic') {
        createdMemory = await apiService.createAudioMemory({
          audioFile: pendingAudio.blob,
          filename: pendingAudio.filename,
          content: trimmed,
          tags,
          source: 'workspace_ui_audio_recording',
          category: 'audio',
          importance: 5,
          spaceId: spaceContext?.space_id,
        });
      } else if (spaceContext) {
        createdMemory = await apiService.createMemoryInSpace(
          {
            content: trimmed,
            tags,
            source: 'workspace_ui',
          },
          spaceContext.space_id
        );
      } else {
        createdMemory = await apiService.createMemory({
          content: trimmed,
          tags,
          source: 'workspace_ui',
        });
      }

      try {
        await refreshMemoryViews();
      } catch (refreshErr) {
        console.warn('Failed to refresh memories after create', refreshErr);
      }

      if (spaceContext?.space_id) {
        await queryClient.refetchQueries(
          spaceMemoryCountQueryKey(spaceContext.space_id)
        );
      }

      const preview =
        createdMemory.content.length > 30
          ? `${createdMemory.content.slice(0, 30)}...`
          : createdMemory.content;
      showFeedback({
        type: 'success',
        title: t('feedback.createSuccess.title', { ns: 'memories' }) as string,
        description: t('feedback.createSuccess.description', {
          ns: 'memories',
          preview,
        }) as string,
        duration: 4000,
      });

      const creationTime = performance.now() - startTime;
      analytics.trackMemoryCreation({
        content: createdMemory.content,
        tags: createdMemory.tags,
      });

      trackMemoryCreated(
        createdMemory.content.slice(0, 50) +
          (createdMemory.content.length > 50 ? '...' : ''),
        spaceContext?.space_id,
        spaceContext?.name
      );

      analytics.trackEvent('memory_creation_completed', {
        memoryId: createdMemory.id,
        creationTime,
        success: true,
        spaceId: spaceContext?.space_id,
        spaceName: spaceContext?.name,
        hasAudio: Boolean(pendingAudio),
      });

      setNewMemory('');
      setNewTags('');
      setPendingAudio(null);
    } catch (error: unknown) {
      console.error('Failed to create memory:', error);
      const msg = extractErrorMessage(
        error,
        t('errors.createFailed', { ns: 'memories' }) as string
      );
      setErrors({ memory: msg });

      analytics.trackEvent('memory_creation_failed', {
        reason: 'api_error',
        error: msg,
        spaceId: spaceContext?.space_id,
      });
    } finally {
      setIsCreating(false);
    }
  };

  const deleteMemory = async (memoryId: string) => {
    try {
      await apiService.deleteMemory(memoryId, spaceContext?.space_id);

      queryClient.setQueryData(infiniteQueryKey, (old) =>
        applyMemoryRemovedFromInfiniteData(
          old as InfiniteData<MemoryList> | undefined,
          memoryId
        )
      );

      if (searchQueryHook.isSearching && searchQuery.trim()) {
        await searchQueryHook.search(searchQuery.trim());
      }

      if (spaceContext?.space_id) {
        queryClient.invalidateQueries(spaceMemoryCountQueryKey(spaceContext.space_id));
      }

      analytics.trackEvent('memory_deleted', {
        memoryId,
        success: true,
        spaceId: spaceContext?.space_id,
        spaceName: spaceContext?.name
      });
    } catch (error: any) {
      console.error('Failed to delete memory:', error);
      analytics.trackEvent('memory_deleted', {
        memoryId,
        success: false,
        error: error.detail,
        spaceId: spaceContext?.space_id
      });
    }
  };

  const exportMemories = async () => {
    try {
      const payload = await apiService.exportMemories(spaceContext?.space_id);
      const fileName = `memara-memories-${spaceContext?.space_id || 'all'}-${new Date()
        .toISOString()
        .slice(0, 10)}.json`;
      const blob = new Blob([JSON.stringify(payload, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Failed to export memories:', error);
      setErrors({
        memory: (t('errors.exportFailed', { ns: 'memories' }) ||
          'Failed to export memories') as string,
      });
    }
  };

  const handleImportFile = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      const fileText = await file.text();
      const parsed = JSON.parse(fileText);
      const importItems = Array.isArray(parsed?.memories) ? parsed.memories : [];

      if (importItems.length === 0) {
        setErrors({
          memory: (t('errors.invalidImportFile', { ns: 'memories' }) ||
            'Invalid import file format') as string,
        });
        return;
      }

      const result = await apiService.importMemories(
        importItems,
        spaceContext?.space_id
      );
      await refreshMemoryViews();

      if (result.failed > 0) {
        setErrors({
          memory: (t('errors.importPartial', {
            ns: 'memories',
            imported: result.imported,
            failed: result.failed,
          }) ||
            `Imported ${result.imported}, failed ${result.failed}`) as string,
        });
      } else {
        setErrors({ memory: '' });
      }
    } catch (error: any) {
      console.error('Failed to import memories:', error);
      setErrors({
        memory: (t('errors.importFailed', { ns: 'memories' }) ||
          'Failed to import memories') as string,
      });
    } finally {
      event.target.value = '';
    }
  };

  const confirmDeleteMemory = (memoryId: string) => {
    setDeleteConfirmation({ isOpen: true, memoryId });
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirmation.memoryId) {
      await deleteMemory(deleteConfirmation.memoryId);
      setDeleteConfirmation({ isOpen: false, memoryId: null });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmation({ isOpen: false, memoryId: null });
  };

  const highlightText = (text: string, query: string) => {
    if (!query) return text;
    
    const regex = new RegExp(`(${query})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <mark key={index} className="bg-workspace-primary-200 text-workspace-primary-800 px-1 rounded">
          {part}
        </mark>
      ) : part
    );
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      analytics.trackEvent('keyboard_shortcut_used', {
        shortcut: 'cmd_enter',
        action: 'create_memory',
        realm: 'workspace'
      });
      createMemory();
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (value.length > 0) {
      analytics.trackEvent('search_query_typed', {
        queryLength: value.length
      });
    }
  };

  const handleMemoryClick = (memoryId: string) => {
    analytics.trackMemoryInteraction('memory_clicked', memoryId, {
      searchQuery: searchQuery || null
    });
    
    setSelectedMemoryId(memoryId);
    setIsModalOpen(true);
  };

  // Modal handlers
  const handleMemoryUpdate = (updatedMemory: Memory) => {
    queryClient.setQueryData(infiniteQueryKey, (old) =>
      applyMemoryUpdatedToInfiniteData(
        old as InfiniteData<MemoryList> | undefined,
        updatedMemory
      )
    );
  };

  const handleMemoryDeleteFromModal = (memoryId: string) => {
    queryClient.setQueryData(infiniteQueryKey, (old) =>
      applyMemoryRemovedFromInfiniteData(
        old as InfiniteData<MemoryList> | undefined,
        memoryId
      )
    );
    if (searchQueryHook.isSearching && searchQuery.trim()) {
      void searchQueryHook.search(searchQuery.trim());
    }
    setIsModalOpen(false);
    setSelectedMemoryId(null);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedMemoryId(null);
  };

  // ✅ FIXED: Removed duplicate useEffect that was causing infinite reload loop
  // Now using single useEffect to handle memory loading on mount and space changes

  // Browse list is already filtered server-side; search results apply refinements client-side
  const filteredMemories = useMemo(() => {
    if (!searchQueryHook.isSearching) {
      return memories;
    }
    return memories.filter((memory) => {
      if (
        selectedTagFilters.length > 0 &&
        !selectedTagFilters.some((tag) => memory.tags.includes(tag))
      ) {
        return false;
      }
      if (selectedSourceFilter !== 'all' && memory.source !== selectedSourceFilter) {
        return false;
      }
      if (minImportanceFilter !== null) {
        return (memory.importance ?? 0) >= minImportanceFilter;
      }
      return true;
    });
  }, [
    memories,
    searchQueryHook.isSearching,
    selectedTagFilters,
    selectedSourceFilter,
    minImportanceFilter,
  ]);

  const memorySectionTotalCount = searchQueryHook.isSearching
    ? filteredMemories.length
    : browseListTotal;

  const isSearchActive =
    Boolean(searchQuery.trim()) || searchQueryHook.isSearching;
  const hasListFilters =
    selectedTagFilters.length > 0 ||
    selectedSourceFilter !== 'all' ||
    minImportanceFilter !== null;
  const listHeaderCount = isSearchActive
    ? filteredMemories.length
    : memorySectionTotalCount;

  // Toggle tag filter
  const toggleTagFilter = (tag: string) => {
    setSelectedTagFilters(prev => {
      const newFilters = prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag];
      
      analytics.trackEvent('tag_filter_toggled', {
        tag,
        isAdded: !prev.includes(tag),
        totalFilters: newFilters.length,
        spaceId: spaceContext?.space_id
      });
      
      return newFilters;
    });
  };

  // Clear all tag filters
  const clearTagFilters = () => {
    setSelectedTagFilters([]);
    analytics.trackEvent('tag_filters_cleared', {
      previousCount: selectedTagFilters.length,
      spaceId: spaceContext?.space_id
    });
  };

  const clearAdvancedFilters = () => {
    clearTagFilters();
    setSelectedSourceFilter('all');
    setMinImportanceFilter(null);
    analytics.trackEvent('advanced_filters_cleared', {
      spaceId: spaceContext?.space_id,
    });
  };

  // ✅ FIX: Don't block UI when loading - show form while loading
  const showLoadingSpinner = isLoading && memories.length === 0;
  
  // FIX: Show loading state when spaceContext is not yet available
  if (spaceContext === undefined) {
    return (
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.4, 0, 0.1, 1] }}
        className="space-y-8"
      >
        <div className="flex items-center justify-center p-12 bg-workspace-card rounded-lg border border-workspace-border">
          <div className="flex flex-col items-center space-y-4">
            <div className="w-8 h-8 border-4 border-workspace-primary-200 border-t-workspace-primary-600 rounded-full animate-spin" />
            <div className="text-center">
              <p className="text-workspace-text-primary font-medium mb-1">Loading Space...</p>
              <p className="text-workspace-text-secondary text-sm">Please wait while we load your memory space</p>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.4, 0, 0.1, 1] }}
      className="space-y-8"
    >
      {/* ✅ FIX: Show loading state without blocking the form */}
      {showLoadingSpinner && (
        <div className="flex items-center justify-center p-4 bg-workspace-card rounded-lg border border-workspace-border">
          <div className="flex items-center space-x-3">
            <div className="w-5 h-5 border-3 border-workspace-primary-200 border-t-workspace-primary-600 rounded-full animate-spin" />
            <span className="text-workspace-text-secondary text-sm">{t('loading', { ns: 'memories' })}</span>
          </div>
        </div>
      )}

      {/* Redis-backed totalMemoryCount: tier/limit UX only; list title uses query totals below. */}
      <UsageWarning
        resourceType="memories"
        currentCount={totalMemoryCount}
      />
      
      {/* Memory Creation Card */}
      <ResourceLimitGate
        resourceType="memories"
        currentCount={totalMemoryCount}
        upgradeContext="memory_limit"
        fallbackType="upgrade-prompt"
      >
        <Card className="bg-white/10 border-white/20 shadow-workspace-soft backdrop-blur-lg">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center space-x-2 text-workspace-text-primary">
            <Plus className="w-5 h-5 text-workspace-primary-500" aria-hidden />
            <span>{t('buttons.captureNew', { ns: 'memories' }) as string}</span>
          </CardTitle>
          <p className="text-sm text-workspace-text-secondary mt-2">
            {t('captureLead', { ns: 'memories' })}
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Textarea
              value={newMemory}
              onChange={(e) => setNewMemory(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder={t('contentPlaceholder', { ns: 'memories' }) as string}
              className="min-h-[100px] bg-workspace-input border-workspace-border text-workspace-text-primary placeholder:text-workspace-text-tertiary focus:border-workspace-primary-400 focus:ring-workspace-primary-400/20 resize-none"
            />
            {errors.memory && (
              <p className="text-red-500 text-sm">{errors.memory}</p>
            )}
          </div>
          
          <div className="space-y-2">
            <Input
              value={newTags}
              onChange={(e) => setNewTags(e.target.value)}
              placeholder={t('tagsPlaceholder', { ns: 'memories' }) as string}
              className="bg-workspace-input border-workspace-border text-workspace-text-primary placeholder:text-workspace-text-tertiary focus:border-workspace-primary-400 focus:ring-workspace-primary-400/20"
            />
          </div>

          <div className="flex flex-col gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={cn(WORKSPACE_OUTLINE_BUTTON, 'self-start')}
              aria-expanded={showMultimodalRoadmap}
              onClick={() => setShowMultimodalRoadmap((open) => !open)}
            >
              {showMultimodalRoadmap ? (
                <ChevronUp className="w-4 h-4 mr-2 shrink-0" aria-hidden />
              ) : (
                <ChevronDown className="w-4 h-4 mr-2 shrink-0" aria-hidden />
              )}
              {showMultimodalRoadmap
                ? (t('roadmapCollapse', { ns: 'memories' }) as string)
                : (t('roadmapExpand', { ns: 'memories' }) as string)}
            </Button>
            {showMultimodalRoadmap && (
              <div className="space-y-4 bg-workspace-surface-tertiary/50 border border-workspace-border rounded-lg p-4">
                <div>
                  <h6 className="text-sm font-medium text-workspace-text-primary mb-2">
                    {t('audio.sectionTitle', { ns: 'memories' }) as string}
                  </h6>
                  <MemoryWorkspaceAudioAttach
                    canUploadAudio={canUploadAudio}
                    disabled={isCreating}
                    currentTier={currentTier}
                    nextTier={nextTier}
                    pending={pendingAudio}
                    onPendingChange={setPendingAudio}
                    onNavigateUpgrade={() => navigate('/billing/upgrade')}
                  />
                </div>
                <div>
                  <h6 className="text-sm font-medium text-workspace-text-primary mb-3">
                    {t('contentTypes', { ns: 'memories' }) as string}
                  </h6>
                  {/* Example multimodal capability strip; claude_desktop until integration context is wired per workspace */}
                  <MemoryTypesBadges integrationId="claude_desktop" compact={true} />
                  <p className="text-xs text-workspace-text-tertiary mt-2">
                    {t('multimodalComingSoon', { ns: 'memories' }) as string}
                  </p>
                </div>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <Button
                    variant="outline"
                    size="sm"
                    type="button"
                    asChild
                    className={cn(WORKSPACE_OUTLINE_BUTTON, 'w-fit')}
                  >
                    <Link to="/roadmap/image-memories#image-interest">
                      {t('imagesEarlyAccess', { ns: 'memories' })}
                    </Link>
                  </Button>
                </div>
              </div>
            )}
          </div>
          
          <div className="flex justify-end items-center">
            <Button
              onClick={createMemory}
              disabled={
                isCreating ||
                !newMemory.trim() ||
                newMemory.trim().length < 3
              }
              className="bg-workspace-primary-500 hover:bg-workspace-primary-600 text-white px-6"
            >
              {isCreating ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>{t('saving', { ns: 'memories' })}</span>
                </div>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  {t('rememberThis', { ns: 'memories' })}
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
      </ResourceLimitGate>

      {/* Search and Filters */}
      <Card className="bg-white/10 border-white/20 shadow-workspace-soft backdrop-blur-lg">
        <CardContent className="p-6">
          <div className="flex items-center space-x-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-workspace-text-tertiary" />
              <Input
                ref={searchInputRef}
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder={t('searchPlaceholder', { ns: 'memories' }) as string}
                className="pl-10 bg-workspace-input border-workspace-border text-workspace-text-primary placeholder:text-workspace-text-tertiary focus:border-workspace-primary-400 focus:ring-workspace-primary-400/20"
              />
            </div>
            
            {searchQuery && (
              <Button
                onClick={() => setSearchQuery('')}
                variant="outline"
                size="sm"
                className={WORKSPACE_OUTLINE_BUTTON}
              >
                {t('clear', { ns: 'memories' })}
              </Button>
            )}
            <div className="flex items-center gap-2">
              <Button
                onClick={exportMemories}
                variant="outline"
                size="sm"
                className={WORKSPACE_OUTLINE_BUTTON}
              >
                <Download className="w-4 h-4 mr-1" />
                {t('actions.export', { ns: 'memories' })}
              </Button>
              <Button
                asChild
                variant="outline"
                size="sm"
                className={WORKSPACE_OUTLINE_BUTTON}
              >
                <label className="cursor-pointer">
                  <Upload className="w-4 h-4 mr-1 inline-block" />
                  {t('actions.import', { ns: 'memories' })}
                  <input
                    type="file"
                    accept="application/json"
                    className="hidden"
                    onChange={handleImportFile}
                  />
                </label>
              </Button>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Select
              value={selectedSourceFilter}
              onValueChange={(value) => {
                setSelectedSourceFilter(value);
                analytics.trackEvent('memory_source_filter_changed', {
                  source: value,
                  spaceId: spaceContext?.space_id,
                });
              }}
            >
              <SelectTrigger className="h-9 w-[180px] bg-workspace-input border-workspace-border text-workspace-text-primary">
                <SelectValue
                  placeholder={t('advancedFilters.source.placeholder', { ns: 'memories' }) as string}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  {t('advancedFilters.source.all', { ns: 'memories' })}
                </SelectItem>
                {allSources.map((source) => (
                  <SelectItem key={source} value={source}>
                    {source}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={minImportanceFilter === null ? 'all' : String(minImportanceFilter)}
              onValueChange={(value) => {
                const parsedValue = value === 'all' ? null : Number(value);
                setMinImportanceFilter(parsedValue);
                analytics.trackEvent('memory_importance_filter_changed', {
                  minImportance: parsedValue,
                  spaceId: spaceContext?.space_id,
                });
              }}
            >
              <SelectTrigger className="h-9 w-[200px] bg-workspace-input border-workspace-border text-workspace-text-primary">
                <SelectValue
                  placeholder={t('advancedFilters.minImportance.placeholder', { ns: 'memories' }) as string}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  {t('advancedFilters.minImportance.all', { ns: 'memories' })}
                </SelectItem>
                {[3, 5, 7, 9].map((level) => (
                  <SelectItem key={level} value={String(level)}>
                    {t('advancedFilters.minImportance.option', {
                      ns: 'memories',
                      level,
                    })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {allTags.length > 0 && (
              <Button
                onClick={() => setShowTagFiltersLocal((prev) => !prev)}
                variant="outline"
                size="sm"
                aria-pressed={shouldShowTagFilters}
                className={cn(
                  WORKSPACE_OUTLINE_BUTTON,
                  shouldShowTagFilters &&
                    'border-workspace-primary-400/40 bg-workspace-primary-500/20 text-workspace-text-primary'
                )}
              >
                <Hash className="w-4 h-4 mr-1" />
                {t('advancedFilters.tags.title', { ns: 'memories' })}
              </Button>
            )}

            {(selectedTagFilters.length > 0 ||
              selectedSourceFilter !== 'all' ||
              minImportanceFilter !== null) && (
              <Button
                onClick={clearAdvancedFilters}
                variant="outline"
                size="sm"
                className={WORKSPACE_OUTLINE_BUTTON}
              >
                {t('advancedFilters.clearAll', { ns: 'memories' })}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tag Filters */}
      <AnimatePresence>
        {shouldShowTagFilters && allTags.length > 0 && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Card className="bg-white/10 border-white/20 shadow-workspace-soft backdrop-blur-lg">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-sm font-medium text-workspace-text-primary flex items-center space-x-2">
                    <Hash className="w-4 h-4" />
                    <span>{t('advancedFilters.tags.title', { ns: 'memories' })}</span>
                  </h4>
                  {selectedTagFilters.length > 0 && (
                    <Button
                      onClick={clearTagFilters}
                      variant="outline"
                      size="sm"
                      className={WORKSPACE_OUTLINE_BUTTON}
                    >
                      {t('advancedFilters.tags.clear', {
                        ns: 'memories',
                        count: selectedTagFilters.length,
                      })}
                    </Button>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {allTags.map((tag) => (
                    <Badge
                      key={tag}
                      variant={selectedTagFilters.includes(tag) ? "default" : "secondary"}
                      className={`cursor-pointer transition-all duration-200 ${
                        selectedTagFilters.includes(tag)
                          ? "bg-workspace-primary-500 text-white hover:bg-workspace-primary-600"
                          : "bg-workspace-surface-tertiary text-workspace-text-secondary hover:bg-workspace-surface-secondary"
                      }`}
                      onClick={() => toggleTagFilter(tag)}
                    >
                      <Hash className="w-3 h-3 mr-1" />
                      {tag}
                    </Badge>
                  ))}
                </div>
                {allTags.length > 0 && (
                  <p className="mt-3 text-xs text-workspace-text-tertiary">
                    {t('advancedFilters.tags.fromLoadedHint', { ns: 'memories' })}
                  </p>
                )}
                {selectedTagFilters.length > 0 && (
                  <div className="mt-3 text-xs text-workspace-text-tertiary">
                    {t('advancedFilters.tags.active', {
                      ns: 'memories',
                      tags: selectedTagFilters.join(', '),
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Memories List */}
      <div className="space-y-4">
        <div>
          <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold text-workspace-text-primary">
            {isSearchActive
              ? t('memory-workspace.memories.searchResults', {
                  count: listHeaderCount,
                  ns: 'memories',
                })
              : hasListFilters
                ? t('memory-workspace.memories.showingFiltered', {
                    count: listHeaderCount,
                    ns: 'memories',
                  })
                : t('memory-workspace.memories.yourMemories', {
                    count: listHeaderCount,
                    ns: 'memories',
                  })}
          </h3>
          
          {/* Sort and View Controls */}
          <div className="flex items-center space-x-3">
            {/* Sort Controls */}
            <div className="flex items-center space-x-2 bg-workspace-surface-tertiary rounded-lg p-1">
              <Select 
                value={sortBy} 
                onValueChange={(value: 'created_at' | 'updated_at' | 'importance') => {
                  setSortBy(value);
                  analytics.trackEvent('memory_sort_changed', {
                    sortBy: value,
                    sortOrder,
                    spaceId: spaceContext?.space_id
                  });
                }}
              >
                <SelectTrigger className="h-8 w-[140px] border-0 bg-transparent text-xs text-workspace-text-primary focus:ring-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="created_at">
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-3 h-3" />
                      <span>{t('sort.createdAt', { ns: 'memories' }) || 'Created'}</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="updated_at">
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-3 h-3" />
                      <span>{t('sort.updatedAt', { ns: 'memories' }) || 'Updated'}</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="importance">
                    <div className="flex items-center space-x-2">
                      <Sparkles className="w-3 h-3" />
                      <span>{t('sort.importance', { ns: 'memories' }) || 'Importance'}</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  const newOrder = sortOrder === 'desc' ? 'asc' : 'desc';
                  setSortOrder(newOrder);
                  analytics.trackEvent('memory_sort_changed', {
                    sortBy,
                    sortOrder: newOrder,
                    spaceId: spaceContext?.space_id
                  });
                }}
                className="h-8 px-2 text-workspace-text-primary hover:bg-workspace-surface-secondary"
              >
                {sortOrder === 'desc' ? (
                  <ArrowDown className="w-4 h-4" />
                ) : (
                  <ArrowUp className="w-4 h-4" />
                )}
              </Button>
            </div>
            
            {/* View Toggle */}
            <div className="flex items-center bg-workspace-surface-tertiary rounded-lg p-1">
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => {
                  setViewMode('list');
                  analytics.trackEvent('memory_view_changed', { 
                    newView: 'list',
                    memoryCount: filteredMemories.length,
                    spaceId: spaceContext?.space_id
                  });
                }}
                className="h-8 px-3"
              >
                <List className="w-4 h-4 mr-1" />
                {t('listView', { ns: 'memories' })}
              </Button>
              <Button
                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => {
                  setViewMode('grid');
                  analytics.trackEvent('memory_view_changed', { 
                    newView: 'grid',
                    memoryCount: filteredMemories.length,
                    spaceId: spaceContext?.space_id
                  });
                }}
                className="h-8 px-3"
              >
                <Grid3X3 className="w-4 h-4 mr-1" />
                {t('gridView', { ns: 'memories' })}
              </Button>
            </div>
          </div>
          </div>
          {!searchQueryHook.isSearching &&
            browseListTotal > 0 &&
            memories.length < browseListTotal && (
              <p className="text-xs text-workspace-text-tertiary mt-1">
                {t('memory-workspace.memories.showingOfTotal', {
                  loaded: memories.length,
                  total: browseListTotal,
                  ns: 'memories',
                })}
              </p>
            )}
        </div>

        {filteredMemories.length === 0 ? (
          <Card className="bg-white/10 border-white/20 shadow-workspace-soft backdrop-blur-lg">
            <CardContent className="p-12 text-center">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
              >
                <div className="w-16 h-16 bg-workspace-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Search className="w-8 h-8 text-workspace-primary-500" />
                </div>
                <h4 className="text-lg font-medium text-workspace-text-primary mb-2">
                  {(searchQuery ||
                    selectedTagFilters.length > 0 ||
                    selectedSourceFilter !== 'all' ||
                    minImportanceFilter !== null)
                    ? t('emptyStates.noMemoriesFound', { ns: 'memories' }) as string 
                    : t('emptyStates.noMemories', { ns: 'memories' }) as string
                  }
                </h4>
                <p className="text-workspace-text-secondary">
                  {(searchQuery ||
                    selectedTagFilters.length > 0 ||
                    selectedSourceFilter !== 'all' ||
                    minImportanceFilter !== null)
                    ? t('emptyStates.noMemoriesFoundDesc', { ns: 'memories' }) as string
                    : t('emptyStates.noMemoriesDesc', { ns: 'memories' }) as string
                  }
                  {(selectedTagFilters.length > 0 ||
                    selectedSourceFilter !== 'all' ||
                    minImportanceFilter !== null) && (
                    <span className="block mt-2">
                      {t('advancedFilters.adjustHint', { ns: 'memories' })}
                    </span>
                  )}
                </p>
              </motion.div>
            </CardContent>
          </Card>
        ) : (
          <>
            <AnimatePresence mode="wait">
              <motion.div
                key={viewMode}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                {viewMode === 'grid' ? (
                  <MemoryGridView
                    memories={filteredMemories}
                    searchQuery={searchQuery}
                    onMemoryClick={handleMemoryClick}
                    onMemoryDelete={confirmDeleteMemory}
                    highlightText={highlightText}
                  />
                ) : (
                  <div className="space-y-4">
                    <AnimatePresence>
                      {filteredMemories.map((memory, index) => (
                        <motion.div
                          key={memory.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          transition={{ duration: 0.3, delay: index * 0.05 }}
                          layout
                        >
                          <Card
                            className="bg-white/10 border-white/20 shadow-workspace-soft backdrop-blur-lg hover:shadow-workspace-medium transition-all duration-200 cursor-pointer group"
                            onClick={() => handleMemoryClick(memory.id)}
                          >
                            <CardContent className="p-6">
                              <div className="flex justify-between items-start mb-3">
                                <div className="flex-1">
                                  <p className="text-workspace-text-primary leading-relaxed">
                                    {highlightText(memory.content, searchQuery)}
                                  </p>
                                </div>
                                <Button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    confirmDeleteMemory(memory.id);
                                  }}
                                  variant="ghost"
                                  size="sm"
                                  className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 hover:bg-red-50 transition-opacity ml-2"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>

                              <div className="flex items-center justify-between">
                                <div className="flex flex-wrap gap-2">
                                  {memory.tags.map((tag) => (
                                    <Badge
                                      key={tag}
                                      variant="secondary"
                                      className="bg-workspace-primary-100 text-workspace-primary-700 hover:bg-workspace-primary-200 px-2 py-1 text-xs"
                                    >
                                      <Hash className="w-3 h-3 mr-1" />
                                      {tag}
                                    </Badge>
                                  ))}
                                  {memoryHasAudio(memory) && (
                                    <Badge
                                      variant="secondary"
                                      className="text-xs bg-workspace-surface-secondary text-workspace-text-primary"
                                    >
                                      <AudioLines className="w-3 h-3 mr-1 shrink-0" aria-hidden />
                                      {t('audio.badgeShort', { ns: 'memories' })}
                                    </Badge>
                                  )}
                                  {memory.source && (
                                    <Badge variant="outline" className="text-xs text-workspace-text-tertiary">
                                      {memory.source}
                                    </Badge>
                                  )}
                                </div>

                                <div className="flex items-center space-x-2 text-xs text-workspace-text-tertiary">
                                  <Calendar className="w-3 h-3" />
                                  <span>
                                    {new Date(memory.created_at).toLocaleDateString()}
                                  </span>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
            {!searchQueryHook.isSearching && infiniteMemoriesQuery.hasNextPage && (
              <div className="flex flex-col items-center gap-3 pt-6">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className={WORKSPACE_OUTLINE_BUTTON}
                  onClick={() => infiniteMemoriesQuery.fetchNextPage()}
                  disabled={infiniteMemoriesQuery.isFetchingNextPage}
                >
                  {infiniteMemoriesQuery.isFetchingNextPage
                    ? (t('memory-workspace.memories.loadingMore', { ns: 'memories' }) as string)
                    : (t('memory-workspace.memories.loadMore', { ns: 'memories' }) as string)}
                </Button>
                <div
                  ref={loadMoreSentinelRef}
                  className="h-2 w-full shrink-0"
                  aria-hidden
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Memory Detail Modal */}
      <AnimatePresence>
        {isModalOpen && selectedMemoryId && (
          <MemoryDetailModal
            memoryId={selectedMemoryId}
            isOpen={isModalOpen}
            onClose={handleModalClose}
            onMemoryUpdate={handleMemoryUpdate}
            onMemoryDelete={handleMemoryDeleteFromModal}
          />
        )}
      </AnimatePresence>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={deleteConfirmation.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t('confirmations.delete', { ns: 'memories' }) as string || 'Delete Memory'}
        description={t('confirmations.deleteDescription', { ns: 'memories' }) as string || 'Are you sure you want to delete this memory? This action cannot be undone.'}
        confirmText={t('confirmations.deleteConfirm', { ns: 'memories' }) as string || 'Delete'}
        cancelText={t('confirmations.deleteCancel', { ns: 'memories' }) as string || 'Cancel'}
        variant="destructive"
      />
    </motion.div>
  );
} 